import React, { createContext, useContext, useState, useEffect } from "react";
import { authAPI, rememberDevice } from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return context;
};

const savedSession = () => {
    try {
        const token = localStorage.getItem("token");
        const saved = localStorage.getItem("user");
        return token && saved ? JSON.parse(saved) : null;
    } catch {
        return null;
    }
};

export const AuthProvider = ({ children }) => {
    // Start from the saved user and check it in the background: the whole app used to wait one full
    // round trip on "Loading…" for /auth/me on every first load (py-per 2026-11-02). The server still
    // checks the token on every request, and a rejected one signs out here.
    const [user, setUser] = useState(savedSession);
    const loading = false;

    useEffect(() => {
        if (!savedSession()) return;
        authAPI.getMe()
            .then((res) => {
                setUser(res.data);
                localStorage.setItem("user", JSON.stringify(res.data));
            })
            .catch((e) => {
                // Signed out only when the server says no — not on a dropped connection.
                if ([401, 403].includes(e?.response?.status)) logout();
            });
    }, []);

    const startSession = ({ token, user: userData }) => {
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);
        return userData;
    };

    // Returns the user, or {mfaRequired, mfaToken} when a code is needed next.
    const login = async (email, password) => {
        const response = await authAPI.login(email, password);
        if (response.data.mfa_required) return { mfaRequired: true, mfaToken: response.data.mfa_token };
        return startSession(response.data);
    };

    const verify2fa = async (email, mfaToken, code, remember) => {
        const response = await authAPI.verify2fa(mfaToken, code, remember);
        if (response.data.device_token) rememberDevice(email, response.data.device_token);
        return startSession(response.data);
    };

    const refreshUser = async () => {
        const res = await authAPI.getMe();
        setUser(res.data);
        localStorage.setItem("user", JSON.stringify(res.data));
        return res.data;
    };

    const logout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setUser(null);
    };

    const isSRM = user?.role === "SRM";
    const isAM = user?.role === "AM";
    // Sales money on screen: the SRM, unless "Hide sales KWD" is on in My Profile (AMs never).
    const showKwd = isSRM && !user?.hide_sales_kwd;

    return (
        <AuthContext.Provider value={{ user, loading, login, verify2fa, logout, refreshUser, isSRM, isAM, showKwd }}>
            {children}
        </AuthContext.Provider>
    );
};
